import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import {
  AddMessageToTicket,
  CancelTicket,
  getConcernMessengerAvailability,
  getDamageMessengerAvailability,
  getMetadataTicketDetails,
  getNewMessageActionState,
  getPermissions,
  getResidentAppSettings,
  getTicketChat,
  getTicketChatActionState,
  getTicketDetails,
  getTicketSettings,
  GetUnread,
  IHQTicketDetailsResponse,
  IMetadataTicketDetailsResponse,
  LoadMetadataTicketDetails,
  LoadTicketChat,
  LoadTicketDetails,
  TicketingType,
  TicketingVersion,
  TicketIssueType,
  TicketStatusPipe,
  TicketTitlePipe,
} from '@resident-nx/shared';
import { TicketDetailsOverviewHQWebComponent } from '../ticket-details-overview-hq/ticket-details-overview-hq.component';
import { TicketDetailsOverviewMetadataWebComponent } from '../ticket-details-overview-metadata/ticket-details-overview-metadata.component';
import { TicketDetailsHeaderWebComponent } from '../ticket-details-header/ticket-details-header.component';
import { TicketDetailsChatComponent } from '../ticket-details-chat/ticket-details-chat.component';
import { TicketDetailsChatOverviewComponent } from '../ticket-details-chat/ticket-details-chat-overview/ticket-details-chat-overview.component';
import { TicketCancelModalWebComponent } from '../../../../molecules/ticket/ticket-cancel-modal/ticket-cancel-modal.component';
import { ModalService } from '../../../../molecules/modal/modal.service';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';

@Component({
  selector: 'rs-web-ticket-details-page',
  templateUrl: './ticket-details-page.component.html',
  styleUrls: ['./ticket-details-page.component.scss'],
  host: { class: 'offcanvas-container' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TicketDetailsOverviewHQWebComponent,
    TicketDetailsOverviewMetadataWebComponent,
    TicketDetailsHeaderWebComponent,
    TicketDetailsChatOverviewComponent,
    TicketDetailsChatComponent,
    TicketTitlePipe,
    TicketStatusPipe,
  ],
})
export class TicketDetailsPageWebComponent {
  #store = inject(Store);
  private modalService = inject(ModalService);
  private ngbActiveOffcanvas = inject(NgbActiveOffcanvas);
  ticketId = input('');
  ticketIssueType = input<TicketIssueType>(undefined);
  public hideNewMessageCounter = false;
  public chatOpen = signal(false);
  public shouldOpenChatOnLoad = signal(false);
  #residentAppSettings = this.#store.selectSignal(getResidentAppSettings);
  #ticketSettings = this.#store.selectSignal(getTicketSettings);
  readonly ticketChatActionState = this.#store.selectSignal(getTicketChatActionState);
  readonly newMessageActionState = this.#store.selectSignal(getNewMessageActionState);
  readonly ticketChat = this.#store.selectSignal(getTicketChat);
  readonly permissions = this.#store.selectSignal(getPermissions);

  readonly ticketingVersion = computed(() => this.#residentAppSettings().ticketingVersion);
  readonly ticketDetails = computed(() =>
    this.ticketingVersion() === TicketingVersion.METADATA
      ? this.#store.selectSignal(getMetadataTicketDetails)()
      : this.#store.selectSignal(getTicketDetails)()
  );

  readonly messengerEnabled = computed(() =>
    this.#store.selectSignal(
      this.ticketIssueType() === 'PROPERTY_DAMAGE'
        ? getDamageMessengerAvailability
        : getConcernMessengerAvailability
    )()
  );

  readonly messagesReadOnly = computed(() => {
    const issueType = this.ticketIssueType();
    const property = issueType === 'CONCERN' ? 'concernMessagesReadOnly' : 'damageMessagesReadOnly';
    return this.#store.selectSignal(getTicketSettings)()[property];
  });

  // the ticketing type is only configured for damages and concerns
  readonly ticketingType = computed(() => {
    switch (this.ticketIssueType()) {
      case TicketIssueType.PROPERTY_DAMAGE:
        return this.#ticketSettings()?.damageTicketingType;
      case TicketIssueType.CONCERN:
        return this.#ticketSettings()?.concernTicketingType;
      default:
        return undefined;
    }
  });

  readonly hasDeleteButton = computed(() => {
    const version = this.ticketingVersion();
    const details = this.ticketDetails();

    if (!version || !details) return false;

    if (version === TicketingVersion.HIERARCHICAL) {
      return ['OPEN', 'IN_PROGRESS', 'WAITING_FOR_OTHERS'].includes(
        (details as IHQTicketDetailsResponse).state
      );
    } else if (version === TicketingVersion.METADATA) {
      // clients with external metadata ticketing can't cancel tickets
      if (!this.permissions().internalTicketing) {
        return false;
      }
      // FULL_SYNC/EXTERNAL tickets are handled in the client's own system and can't be cancelled
      if (this.ticketingType() !== TicketingType.INTERNAL) {
        return false;
      }
      return ['OPEN', 'IN_PROGRESS', 'WAITING_FOR_OTHERS'].includes(
        (details as IMetadataTicketDetailsResponse).status
      );
    }
    return false;
  });

  constructor() {
    effect(() => {
      const version = this.ticketingVersion();

      if (!version) return;

      untracked(() => {
        const data = {
          ticketId: this.ticketId(),
          ticketIssueType: this.ticketIssueType(),
        };

        if (version === TicketingVersion.METADATA) {
          this.#store.dispatch(LoadMetadataTicketDetails(data));
        } else {
          this.#store.dispatch(LoadTicketDetails(data));
        }
      });
    });

    const chatOpenEff = effect(() => {
      const chatOpen = this.chatOpen();

      untracked(() => {
        if (chatOpen) {
          this.loadTicketChatDetails();
          chatOpenEff.destroy();
        }
      });
    });

    const shouldOpenChatOnLoadEff = effect(() => {
      const shouldOpenChatOnLoad = this.shouldOpenChatOnLoad();

      untracked(() => {
        if (shouldOpenChatOnLoad) {
          this.openChat();
          shouldOpenChatOnLoadEff.destroy();
        }
      });
    });

    const ticketChatEff = effect(() => {
      const chat = this.ticketChat();

      untracked(() => {
        if (chat.length > 0 && this.ticketDetails().unreadMessages > 0) {
          this.#store.dispatch(GetUnread());
          ticketChatEff.destroy();
        }
      });
    });
  }

  public onCancelTicket() {
    const modalRef = this.modalService.open(TicketCancelModalWebComponent, { centered: true });
    modalRef.onClose().subscribe(() => {
      this.#store.dispatch(
        CancelTicket({
          ticketId: this.ticketId(),
          ticketIssueType: this.ticketIssueType(),
        })
      );
      this.ngbActiveOffcanvas.close();
    });
  }

  public onClose() {
    this.ngbActiveOffcanvas.close();
  }

  public openChat() {
    this.chatOpen.set(true);
    this.hideNewMessageCounter = true;
  }

  public sendMessage(newMessage: string): void {
    const message = {
      ticketId: this.ticketId(),
      message: newMessage,
    };

    this.#store.dispatch(AddMessageToTicket({ message }));
  }

  public retryMessage(messageId: string) {
    const message = {
      ticketId: this.ticketId(),
      retryOfConversationMessageId: messageId,
    };

    this.#store.dispatch(AddMessageToTicket({ message }));
  }

  public loadTicketChatDetails() {
    this.#store.dispatch(LoadTicketChat({ ticketId: this.ticketId() }));
  }
}
